import React from 'react';
import { Button } from 'react-bootstrap';
import { CheckBox } from 'devextreme-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import Label from 'components/wrapped-component/hint-controls/Label';
import Textbox from 'components/wrapped-component/hint-controls/Textbox';
import toast from 'CustomToast';
import { v4 as uuidv4 } from 'uuid';

export type Pattern = {
  id: string;
  rows: number;
  cols: number;
  grid: boolean[][];
};

export type DrawPatternState = {
  inputRows: number | null;
  inputCols: number | null;
  patterns: Pattern[];
  activePatternIndex: number;
  isDrawing: boolean;
  drawState: boolean;
  isEraserMode: boolean;
};

type DrawPatternProps = {
  drawPattern: DrawPatternState;
  onChange: (newState: DrawPatternState) => void;
};

class PatternThumbnail extends React.Component<{ pattern: any, isActive: boolean, onClick: () => void, onDelete: (e: any) => void }> {
  canvasRef = React.createRef<HTMLCanvasElement>();

  componentDidMount() {
    this.draw();
  }

  componentDidUpdate(prevProps: any) {
    if (prevProps.pattern !== this.props.pattern) {
      this.draw();
    }
  }

  draw = () => {
    const canvas = this.canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const { pattern } = this.props;
    const { rows, cols, grid } = pattern;
    
    canvas.width = cols;
    canvas.height = rows;
    
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, cols, rows);
    
    ctx.fillStyle = '#666';
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (grid[r]?.[c]) {
          ctx.fillRect(c, r, 1, 1);
        }
      }
    }
  }

  render() {
    const { pattern, isActive, onClick, onDelete } = this.props;
    return (
      <div 
        onClick={onClick}
        className={`pattern-thumbnail ${isActive ? 'active' : ''}`}
      >
        <div className="thumbnail-title">
          {pattern.id}
        </div>
        <div className="thumbnail-canvas-container">
           <canvas
             ref={this.canvasRef}
             className="thumbnail-canvas"
           />
        </div>
        <div 
          onClick={onDelete}
          className="delete-icon"
        >
          <FontAwesomeIcon icon={faTrash} size="xs" />
        </div>
      </div>
    );
  }
}

export default class DrawPattern extends React.Component<DrawPatternProps> {
  private readonly MAX_PATTERN_DIMENSION = 1000;
  drawPatternCanvasRef: React.RefObject<HTMLCanvasElement> = React.createRef();
  lastDrawRow: number | null = null;
  lastDrawCol: number | null = null;

  isValidDimension = (value: number | null): value is number =>
  value !== null &&
  Number.isInteger(value) &&
  value > 0 &&
  value <= this.MAX_PATTERN_DIMENSION;

  componentDidMount() {
    this.drawPatternCanvas();
  }

  componentDidUpdate(prevProps: DrawPatternProps) {
    if (prevProps.drawPattern !== this.props.drawPattern) {
      this.drawPatternCanvas();
    }
  }

  handleDrawPatternInputChange = (key: 'inputRows' | 'inputCols', value: string) => {
    const numValue = value === "" ? null : parseInt(value, 10);
    if (
      value === "" ||
      (numValue !== null &&
        numValue > 0 &&
        numValue <= this.MAX_PATTERN_DIMENSION)
    ) {
      this.props.onChange({
        ...this.props.drawPattern,
        [key]: numValue
      });
    }
  };

  handleAddPattern = () => {
    const { inputRows, inputCols } = this.props.drawPattern;
    if (this.isValidDimension(inputRows) && this.isValidDimension(inputCols)) {
      const newGrid: boolean[][] = [];
      for (let i = 0; i < inputRows; i += 1) {
        newGrid.push(new Array(inputCols).fill(false));
      }
      
      const newPattern = {
        id: `DEVICE-${uuidv4().substring(0, 8).toUpperCase()}`,
        rows: inputRows,
        cols: inputCols,
        grid: newGrid
      };

      this.props.onChange({
        ...this.props.drawPattern,
        patterns: [...this.props.drawPattern.patterns, newPattern],
        activePatternIndex: this.props.drawPattern.patterns.length
      });
    } else {
      toast.warning(`Please enter integers between 1 and ${this.MAX_PATTERN_DIMENSION} for Rows and Columns.`);
    }
  };

  handleDrawPattern = () => {
    const { inputRows, inputCols, patterns, activePatternIndex } = this.props.drawPattern;

    if (this.isValidDimension(inputRows) && this.isValidDimension(inputCols)) {
      const newGrid: boolean[][] = [];
      for (let i = 0; i < inputRows; i += 1) {
        newGrid.push(new Array(inputCols).fill(false));
      }

      if (activePatternIndex !== -1 && patterns[activePatternIndex]) {
        const newPatterns = [...patterns];
        newPatterns[activePatternIndex] = {
          ...newPatterns[activePatternIndex],
          rows: inputRows,
          cols: inputCols,
          grid: newGrid
        };

        this.props.onChange({
          ...this.props.drawPattern,
          patterns: newPatterns
        });
      } else {
        const newPattern = {
          id: `DEVICE-${uuidv4().substring(0, 8).toUpperCase()}`,
          rows: inputRows,
          cols: inputCols,
          grid: newGrid
        };

        this.props.onChange({
          ...this.props.drawPattern,
          patterns: [...this.props.drawPattern.patterns, newPattern],
          activePatternIndex: this.props.drawPattern.patterns.length
        });
      }
    } else {
      toast.warning(`Please enter integers between 1 and ${this.MAX_PATTERN_DIMENSION} for Rows and Columns.`);
    }
  };

  handleDeletePattern = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const { patterns, activePatternIndex } = this.props.drawPattern;
    
    const newPatterns = [...patterns];
    newPatterns.splice(index, 1);
    
    let newActiveIndex = activePatternIndex;
    if (index === newActiveIndex) {
      newActiveIndex = newPatterns.length > 0 ? 0 : -1;
    } else if (index < newActiveIndex) {
      newActiveIndex -= 1;
    }

    this.props.onChange({
      ...this.props.drawPattern,
      patterns: newPatterns,
      activePatternIndex: newActiveIndex
    });
  };

  handlePatternSelect = (index: number) => {
    this.props.onChange({
      ...this.props.drawPattern,
      activePatternIndex: index
    });
  };

  getPointsOnLine = (r0: number, c0: number, r1: number, c1: number) => {
    const points = [];
    let x0 = c0;
    let y0 = r0;
    const x1 = c1;
    const y1 = r1;
    
    const dx = Math.abs(x1 - x0);
    const dy = Math.abs(y1 - y0);
    const sx = (x0 < x1) ? 1 : -1;
    const sy = (y0 < y1) ? 1 : -1;
    let err = dx - dy;
    
    while (true) {
        points.push({r: y0, c: x0});
        if (x0 === x1 && y0 === y1) break;
        const e2 = 2 * err;
        if (e2 > -dy) { err -= dy; x0 += sx; }
        if (e2 < dx) { err += dx; y0 += sy; }
    }
    return points;
  };

  drawPatternCanvas = () => {
    const canvas = this.drawPatternCanvasRef.current;
    if (!canvas) return;

    const { patterns, activePatternIndex } = this.props.drawPattern;
    if (activePatternIndex === -1 || !patterns[activePatternIndex]) {
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    const pattern = patterns[activePatternIndex];
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const cellSize = 10;
    const gap = 2;
    const width = pattern.cols * (cellSize + gap) + gap;
    const height = pattern.rows * (cellSize + gap) + gap;

    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    ctx.fillStyle = '#ccc';
    ctx.fillRect(0, 0, width, height);

    for (let r = 0; r < pattern.rows; r++) {
      for (let c = 0; c < pattern.cols; c++) {
        const cell = Boolean(pattern.grid[r]?.[c]);
        const x = gap + c * (cellSize + gap);
        const y = gap + r * (cellSize + gap);
        
        ctx.fillStyle = cell ? '#666' : '#fff';
        ctx.fillRect(x, y, cellSize, cellSize);
      }
    }
  };

  handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const { patterns, activePatternIndex, isEraserMode } = this.props.drawPattern;
    if (activePatternIndex === -1 || !patterns[activePatternIndex]) return;

    const canvas = this.drawPatternCanvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const cellSize = 10;
    const gap = 2;
    const totalCellSize = cellSize + gap;

    const colIndex = Math.floor((x - gap) / totalCellSize);
    const rowIndex = Math.floor((y - gap) / totalCellSize);
    
    const pattern = patterns[activePatternIndex];

    if (rowIndex < 0 || rowIndex >= pattern.rows || colIndex < 0 || colIndex >= pattern.cols) return;

    const currentGrid = pattern.grid;
    const newDrawState = !isEraserMode;
    this.lastDrawRow = rowIndex;
    this.lastDrawCol = colIndex;
    
    const newGrid = currentGrid.map((row, r) => 
      row.map((cell, c) => (r === rowIndex && c === colIndex ? newDrawState : cell))
    );
    
    const newPatterns = [...patterns];
    newPatterns[activePatternIndex] = {
      ...newPatterns[activePatternIndex],
      grid: newGrid
    };

    this.props.onChange({
      ...this.props.drawPattern,
      isDrawing: true,
      drawState: newDrawState,
      patterns: newPatterns
    });
  };

  handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const { isDrawing, drawState, patterns, activePatternIndex } = this.props.drawPattern;
    if (!isDrawing || activePatternIndex === -1 || !patterns[activePatternIndex]) return;

    const canvas = this.drawPatternCanvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const cellSize = 10;
    const gap = 2;
    const totalCellSize = cellSize + gap;

    const colIndex = Math.floor((x - gap) / totalCellSize);
    const rowIndex = Math.floor((y - gap) / totalCellSize);
    
    const pattern = patterns[activePatternIndex];

    if (rowIndex < 0 || rowIndex >= pattern.rows || colIndex < 0 || colIndex >= pattern.cols) return;

    const currentGrid = pattern.grid;
    let pointsToUpdate = [{r: rowIndex, c: colIndex}];

    if (this.lastDrawRow !== null && this.lastDrawCol !== null) {
      pointsToUpdate = this.getPointsOnLine(this.lastDrawRow, this.lastDrawCol, rowIndex, colIndex);
    }

    let hasChanges = false;
    const newGrid = currentGrid.map(row => [...row]);

    pointsToUpdate.forEach(point => {
      if (newGrid[point.r] && newGrid[point.r][point.c] !== drawState) {
        newGrid[point.r][point.c] = drawState;
        hasChanges = true;
      }
    });

    this.lastDrawRow = rowIndex;
    this.lastDrawCol = colIndex;

    if (hasChanges) {
      const newPatterns = [...patterns];
      newPatterns[activePatternIndex] = {
        ...newPatterns[activePatternIndex],
        grid: newGrid
      };

      this.props.onChange({
        ...this.props.drawPattern,
        patterns: newPatterns
      });
    }
  };

  handleGridMouseUp = () => {
    this.lastDrawRow = null;
    this.lastDrawCol = null;
    this.props.onChange({
      ...this.props.drawPattern,
      isDrawing: false
    });
  };

  render() {
    const { drawPattern } = this.props;
    
    return (
      <div className="custom-form draw-pattern-container" onMouseUp={this.handleGridMouseUp} onMouseLeave={this.handleGridMouseUp}>
        <div className="draw-pattern-controls">
          <div className="control-item">
            <Label labelTitle="Rows:" labelPosition="top" labelSize="20" fieldSize="80" errorSize="0">
            <Textbox
              name="drawPatternRows"
              type="number"
              min="1"
              placeholder="40"
              value={drawPattern.inputRows ?? ""}
              onChange={(e: any) => this.handleDrawPatternInputChange('inputRows', e.target.value)}
            />
          </Label>
          </div>
          <div className="control-item">
            <Label labelTitle="Columns:" labelPosition="top" labelSize="20" fieldSize="80" errorSize="0">
            <Textbox
              name="drawPatternCols"
              type="number"
              min="1"
              placeholder="50"
              value={drawPattern.inputCols ?? ""}
              onChange={(e: any) => this.handleDrawPatternInputChange('inputCols', e.target.value)}
            />
           </Label>
          </div>
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={this.handleDrawPattern}
            className="draw-button"
          >
            Draw
          </Button>
          <div className="erase-checkbox-container">
            <CheckBox
              value={drawPattern.isEraserMode}
              onValueChange={(value) => {
                this.props.onChange({
                  ...drawPattern,
                  isEraserMode: !!value
                });
              }}
              text="Erase"
            />
          </div>
          <Button 
            variant="primary" 
            size="sm" 
            onClick={this.handleAddPattern}
            className="add-button"
          >
            Add
          </Button>
        </div>

        <div className="draw-pattern-main">
          <div className="pattern-thumbnails-list">
            {drawPattern.patterns.map((pattern, index) => (
              <PatternThumbnail
                key={pattern.id}
                pattern={pattern}
                isActive={index === drawPattern.activePatternIndex}
                onClick={() => this.handlePatternSelect(index)}
                onDelete={(e) => this.handleDeletePattern(index, e)}
              />
            ))}
            {drawPattern.patterns.length === 0 && (
              <div className="no-patterns-message">
                No patterns added
              </div>
            )}
          </div>
          <div className="draw-canvas-container">
            <div className="draw-canvas-wrapper">
            {drawPattern.activePatternIndex !== -1 && drawPattern.patterns[drawPattern.activePatternIndex] ? (
              <canvas
                ref={this.drawPatternCanvasRef}
                onMouseDown={this.handleCanvasMouseDown}
                onMouseMove={this.handleCanvasMouseMove}
                onMouseLeave={this.handleGridMouseUp}
                className="draw-canvas"
              />
            ) : (
              <div className="no-pattern-selected-message">
                Select or add a pattern to draw
              </div>
            )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}